<template>
  <div v-if="error">
    Произошла ошибка. Обновите страницу.
  </div>
  <div v-else-if="!isLoad" class="mc-loader loader">
    <div class="mc-loader"><span class="mc-loader__circle"></span></div>
  </div>
  <component v-else :is="layout">
    <router-view/>
  </component>
</template>

<script>
import EmptyLayout from '@/layouts/EmptyLayout'
import MainLayout from '@/layouts/MainLayout'
import axios from 'axios'
import { firebaseService } from '@/services/firebase'
import { userService } from '@/services/user'
import { simulatedHoverMixin } from './mixin/simulatedHoverMixin'

const AUTH_REFRESH_INTERVAL = 5 * 60 * 1000
const LONG_IDLE_INTERVAL = 15 * 60 * 1000

export default {
  data: () => ({
    isLoad: false,
    device: '',
    device_uuid: '',
    authRefreshTimer: null,
    unregisterBasketGuard: null,
    authRefreshPromise: null,
    lastAuthRefreshAt: 0,
    lastActivityAt: Date.now(),
    handleVisibilityChange: null,
    handleWindowFocus: null,
    handleUserActivity: null,
  }),
  mixins: [simulatedHoverMixin],
  computed: {
    layout() {
      return (this.$route.meta.layout || 'empty') + '-layout'
    },
    error() {
      return this.$store.getters.errorRequest
    }
  },

  components: {
    EmptyLayout,
    MainLayout
  },

  async created() {
    const { device_uuid, device } = this.$attrs
    this.device_uuid = device_uuid
    this.device = device

    try {
      await this.refreshUserData({ force: true, useInitialAuthAction: true })

      this.setupAxiosInterceptors()
      this.setupAuthRefresh()

      if (typeof window.FirebasePlugin !== 'undefined') {
        firebaseService.initializeMessageHandling(this.$store, this.$router)
      }

      this.checkRouteAccess()
    } catch (error) {
      console.error('App initialization error:', error)
    } finally {
      this.isLoad = true
    }
  },

  beforeUnmount() {
    this.teardownAuthRefresh()
  },

  methods: {
    async refreshUserData({ force = false, useInitialAuthAction = false } = {}) {
      const now = Date.now()

      if (!force && now - this.lastAuthRefreshAt < AUTH_REFRESH_INTERVAL) {
        return
      }

      if (this.authRefreshPromise) {
        return this.authRefreshPromise
      }

      this.authRefreshPromise = (async () => {
        const response = await userService.register({
          device: this.device,
          device_uuid: this.device_uuid
        })

        const userData = {
          ...response.data.data,
          device: this.device,
          uuid: this.device_uuid
        }

        if (useInitialAuthAction) {
          await this.$store.dispatch('Auth', userData)
        } else {
          await this.$store.dispatch('AuthRepeat', userData)
        }

        await this.$store.dispatch('counter/fetchCounterData', true)

        this.lastAuthRefreshAt = Date.now()
      })()

      try {
        return await this.authRefreshPromise
      } catch (error) {
        console.error('Auth refresh error:', error)
      } finally {
        this.authRefreshPromise = null
      }
    },

    setupAuthRefresh() {
      this.authRefreshTimer = setInterval(() => {
        this.refreshUserData({ force: true })
      }, AUTH_REFRESH_INTERVAL)

      this.unregisterBasketGuard = this.$router.beforeEach(async (to) => {
        if (to.name === 'basket' || to.meta.basket_page === true) {
          await this.refreshUserData({ force: true })
        }
      })

      this.handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          this.refreshUserData({ force: true })
        }
      }

      this.handleWindowFocus = () => {
        this.refreshUserData({ force: true })
      }

      this.handleUserActivity = () => {
        const now = Date.now()
        if (now - this.lastActivityAt >= LONG_IDLE_INTERVAL) {
          this.refreshUserData({ force: true })
        }
        this.lastActivityAt = now
      }

      document.addEventListener('visibilitychange', this.handleVisibilityChange)
      window.addEventListener('focus', this.handleWindowFocus)
      ;['click', 'touchstart', 'keydown', 'mousemove', 'scroll'].forEach((eventName) => {
        window.addEventListener(eventName, this.handleUserActivity, { passive: true })
      })
    },

    teardownAuthRefresh() {
      if (this.authRefreshTimer) {
        clearInterval(this.authRefreshTimer)
      }

      if (this.unregisterBasketGuard) {
        this.unregisterBasketGuard()
      }

      if (this.handleVisibilityChange) {
        document.removeEventListener('visibilitychange', this.handleVisibilityChange)
      }

      if (this.handleWindowFocus) {
        window.removeEventListener('focus', this.handleWindowFocus)
      }

      if (this.handleUserActivity) {
        ;['click', 'touchstart', 'keydown', 'mousemove', 'scroll'].forEach((eventName) => {
          window.removeEventListener(eventName, this.handleUserActivity)
        })
      }
    },

    setupAxiosInterceptors() {
      this.axios.interceptors.response.use(
        response =>
        {
          if (response.data.status === 'error') {
            console.log(response.data.errors);
          }
          return response;
        },
        error => {
          if (error.response?.status === 401) {
            this.$store.commit('logout')

            if (this.$route.meta.requiresAuth === true) {
              this.$router.push('/auth/')
            }
          }
          if (error.response?.status === 500) {
            this.$router.push('/notfound')
          }
          return Promise.reject(error)
        }
      )
    },

    checkRouteAccess() {
      const currentRoute = this.$router.currentRoute.value

      if (currentRoute.meta.requiresAuth === true && !this.$store.getters.isAuth) {
        this.$router.push('/auth/')
      } else if (currentRoute.meta.requiresAuth === false && this.$store.getters.isAuth) {
        this.$router.push('/')
      }
    }
  }
}
</script>

<style scoped>
.loader {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  text-align: center;
}

.spinner {
  height: 40px;
  border: 4px solid rgba(0, 0, 0, 0.2);
  border-radius: 50%;
  border-top-color: #ddd;
  animation: spiner 1s ease-in-out infinite;
  margin-bottom: 16px;
}



</style>
